import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "app/store";
import { useDialog, useRecordEvent } from "app/hook";
import { AppPlatform, DialogType, AnalyticsEventType } from "app/types";
import { btnDelay, usePlaySound, sleep } from "common/utils";
import i18n from "features/parent/i18n";
import { createCustomerPortal } from "features/subscription/api";
import { PlanCategory, BillingPeriod } from "features/subscription/useNativePackages";
import {
  PlanType,
  useSubscription,
  useNativePackages,
  fetchAccountPlan,
  createHostedCheckout,
  useSubscriptionPlans,
} from "features/subscription";
import {
  PlanInfoSection,
  UpgradeSection,
  FeatureHeroCards,
  PlanComparisonTable,
  SubscriptionDialogs,
} from "../components";

export const SubscriptionTab = () => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const handleRecordEvent = useRecordEvent();
  const dispatch = useDispatch<AppDispatch>();
  const [update, setUpdate] = useState(false);
  const { plan, entitlement } = useSubscription();
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [showStoreCancelInfo, setShowStoreCancelInfo] = useState(false);
  const user = useSelector((state: RootState) => state.user.parent);
  const activeUser = useSelector((state: RootState) => state.user.active);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [showPlatformMismatchError, setShowPlatformMismatchError] = useState(false);
  const [showAmazonPurchaseWarning, setShowAmazonPurchaseWarning] = useState(false);
  const [showRestoreSuccess, setShowRestoreSuccess] = useState(false);

  const isFree = plan === PlanType.FREE;
  const isPaid =
    plan === PlanType.STANDARD || plan === PlanType.PREMIUM || plan === PlanType.BUILDER;
  const isEducation = plan === PlanType.EDUCATION;
  const platform = window.kidsAppPlatform || AppPlatform.Web;
  const isNativeApp = platform === AppPlatform.iOS || platform === AppPlatform.Android;
  const { requestPackages, getPrice, getIdentifier, status: packagesStatus } = useNativePackages();

  // Web向けプラン情報をReact Queryでキャッシュ取得
  const { data: webPlans = [] } = useSubscriptionPlans("web", isFree && !isNativeApp);

  const getPriceDisplay = (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ): { price: number; priceString: string; currencyCode: string } => {
    if (isNativeApp) {
      return getPrice(planCategory, period);
    }
    // Web: APIから取得したプランから検索
    const targetName = planCategory === "standard" ? "基本プラン" : "特別プラン";
    const foundPlan = webPlans.find(
      (p) => p.name.includes(targetName) && p.billing_period === period,
    );
    if (foundPlan) {
      const intPrice = Math.floor(foundPlan.price);
      return {
        price: intPrice,
        priceString: `¥${intPrice.toLocaleString()}`,
        currencyCode: "JPY",
      };
    }
    // フォールバック
    const fallbackPrices: Record<
      PlanCategory,
      Record<BillingPeriod, { price: number; priceString: string; currencyCode: string }>
    > = {
      standard: {
        monthly: { price: 400, priceString: "¥400", currencyCode: "JPY" },
        annual: { price: 3900, priceString: "¥3,900", currencyCode: "JPY" },
      },
      premium: {
        monthly: { price: 1500, priceString: "¥1,500", currencyCode: "JPY" },
        annual: { price: 14900, priceString: "¥14,900", currencyCode: "JPY" },
      },
    };
    return fallbackPrices[planCategory][period];
  };

  useEffect(() => {
    if (isFree && isNativeApp) {
      requestPackages();
    }
  }, [isFree, isNativeApp, requestPackages]);

  // bfcacheから復元された場合にローディングをクリア（Webブラウザのみ）
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // WebView外（通常のブラウザ）かつbfcacheから復元された場合のみ
      if (event.persisted && !window.ReactNativeWebView) {
        handleDialog({ type: DialogType.LOADING, value: false });
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [handleDialog]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");

    if (sessionId && activeUser?.uid) {
      setIsStripeLoading(true);

      const syncAfterStripeRedirect = async () => {
        try {
          await sleep(3000); // Webhook処理を待つ
          await dispatch(fetchAccountPlan({ uid: user.uid, forceSync: true })).unwrap();
          toast(
            <div>
              <p className="text text-textcolor-black">
                {i18n.t("MSG_SUBSCRIPTION_PURCHASE_SUCCESS")}
              </p>
            </div>,
          );
        } catch (err) {
          console.error("Stripe redirect sync error:", err);
        } finally {
          setIsStripeLoading(false);
          window.history.replaceState({}, "", "/parent?tab=subscription");
        }
      };
      syncAfterStripeRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.uid, dispatch]);

  const getProductId = (planCategory: PlanCategory, period: BillingPeriod): string | undefined => {
    if (isNativeApp) {
      return getIdentifier(planCategory, period);
    }
    const targetName = planCategory === "standard" ? "基本プラン" : "特別プラン";
    const foundPlan = webPlans.find(
      (p) => p.name.includes(targetName) && p.billing_period === period,
    );
    return foundPlan?.product_id;
  };

  const handleRevenueCatPurchase = async (planCategory: PlanCategory) => {
    const productId = getProductId(planCategory, billingPeriod);

    if (!productId) {
      toast(
        <div>
          <p className="text text-textcolor-black">{i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")}</p>
        </div>,
      );
      return;
    }

    handleDialog({ type: DialogType.LOADING, value: true });

    try {
      if (platform === AppPlatform.iOS && Number(window.kidsBuildVersion) > 113) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            command: "subscription",
            data: { uid: user.uid, sku: productId },
          }),
        );
      } else if (platform === AppPlatform.Android && Number(window.kidsBuildVersion) > 87) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            command: "subscription",
            data: { uid: user.uid, sku: productId },
          }),
        );
      } else {
        handleDialog({ type: DialogType.LOADING, value: false });
        setUpdate(true);
      }
    } catch (err) {
      console.error("Failed to create subscription intent:", err);
      handleDialog({ type: DialogType.LOADING, value: false });
      toast(
        <div>
          <p className="text text-textcolor-black">{i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")}</p>
        </div>,
      );
    }
  };

  const handleHostedCheckout = async (planCategory: PlanCategory) => {
    const productId = getProductId(planCategory, billingPeriod);
    if (!productId) {
      toast(
        <div>
          <p className="text text-textcolor-black">{i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")}</p>
        </div>,
      );
      return;
    }

    handleDialog({ type: DialogType.LOADING, value: true });

    try {
      const result = await dispatch(
        createHostedCheckout({
          uid: user.uid,
          product_id: productId,
          success_url: `${window.location.origin}/parent?tab=subscription`,
          cancel_url: `${window.location.origin}/parent?tab=subscription`,
        }),
      ).unwrap();

      // Stripeのホスト型チェックアウトページにリダイレクト
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error("Failed to create hosted checkout:", err);
      handleDialog({ type: DialogType.LOADING, value: false });
      toast(
        <div>
          <p className="text text-textcolor-black">{i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")}</p>
        </div>,
      );
    }
  };

  const handlePurchase = (planCategory: PlanCategory) => {
    play();
    handleRecordEvent(AnalyticsEventType.SUBSCRIPTION_PURCHASE_BTN_CLICK, {
      plan_category: planCategory,
    });
    btnDelay(() => {
      // Amazonプラットフォームの場合は警告ダイアログを表示
      if (platform === AppPlatform.Amazon) {
        setShowAmazonPurchaseWarning(true);
        return;
      }
      if (platform === AppPlatform.iOS || platform === AppPlatform.Android) {
        handleRevenueCatPurchase(planCategory);
      } else {
        handleHostedCheckout(planCategory);
      }
    });
  };

  const handleCloseStoreCancelInfo = () => {
    play();
    btnDelay(() => setShowStoreCancelInfo(false));
  };

  const handleOpenPortal = async () => {
    handleDialog({ type: DialogType.LOADING, value: true });

    try {
      const result = await createCustomerPortal({
        uid: user.uid,
        return_url: `${window.location.origin}/parent?tab=subscription`,
      });
      window.location.href = result.portal_url;
    } catch (err) {
      console.error("Open portal error:", err);
      handleDialog({ type: DialogType.LOADING, value: false });
      toast(
        <div>
          <p className="text text-textcolor-black">{i18n.t("MSG_SUBSCRIPTION_PORTAL_ERROR")}</p>
        </div>,
      );
    }
  };

  const canManageSubscription = (): boolean => {
    const provider = entitlement?.payment_provider;
    if (!provider) return false;

    if (isNativeApp) {
      if (platform === AppPlatform.iOS && provider === "app_store") return true;
      if (platform === AppPlatform.Android && provider === "play_store") return true;
      return false;
    }
    return provider === "stripe";
  };

  const handleManageSubscription = () => {
    play();
    btnDelay(() => {
      if (!canManageSubscription()) {
        setShowPlatformMismatchError(true);
        return;
      }

      if (entitlement?.payment_provider === "stripe") {
        handleOpenPortal();
      } else {
        setShowStoreCancelInfo(true);
      }
    });
  };

  const handleClosePlatformMismatchError = () => {
    play();
    btnDelay(() => setShowPlatformMismatchError(false));
  };

  const handleCloseAmazonPurchaseWarning = () => {
    play();
    btnDelay(() => setShowAmazonPurchaseWarning(false));
  };

  const handleRestorePurchases = async () => {
    play();
    handleDialog({ type: DialogType.LOADING, value: true });
    try {
      await dispatch(fetchAccountPlan({ uid: user.uid, forceSync: true })).unwrap();
      handleDialog({ type: DialogType.LOADING, value: false });
      setShowRestoreSuccess(true);
    } catch {
      handleDialog({ type: DialogType.LOADING, value: false });
      // エラーが発生しても復元は完了扱い（サーバー側で最新の状態に同期済み）
    }
  };

  const handleCloseRestoreSuccess = () => {
    play();
    btnDelay(() => setShowRestoreSuccess(false));
  };

  const handleGotoUpgrade = () => {
    play();
    if (platform === AppPlatform.iOS) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "https://apps.apple.com/jp/app/id1642655214" },
        }),
      );
    } else if (platform === AppPlatform.Android) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "https://play.google.com/store/apps/details?id=com.inexus.app.kids" },
        }),
      );
    } else if (platform === AppPlatform.Amazon) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "http://www.amazon.co.jp/gp/mas/dl/android?p=com.inexus.app.kids" },
        }),
      );
    }
    setUpdate(false);
  };

  const handleCloseUpgrade = () => {
    play();
    btnDelay(() => setUpdate(false));
  };

  useEffect(() => {
    const handleMessage = async (message: MessageEvent) => {
      try {
        const { command, data } = JSON.parse(message.data);
        if (command === "subscription") {
          const { result } = data;

          if (result === "success") {
            await sleep(5000);
            await dispatch(fetchAccountPlan({ uid: user.uid, forceSync: true })).unwrap();
            // fetchLimits は不要: fetchAccountPlan で全子供の limits も取得済み
            handleDialog({ type: DialogType.LOADING, value: false });
            toast(
              <div>
                <p className="text text-textcolor-black">
                  {i18n.t("MSG_SUBSCRIPTION_PURCHASE_SUCCESS")}
                </p>
              </div>,
            );
          } else {
            handleDialog({ type: DialogType.LOADING, value: false });
            toast(
              <div>
                <p className="text text-textcolor-black">
                  {i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")}
                </p>
              </div>,
            );
          }
        }
      } catch {
        // Non-JSON messages are ignored
      }
    };

    if (window.ReactNativeWebView) {
      if (window.kidsAppPlatform === "ios") {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage as EventListener);
      }
    }

    return () => {
      if (window.ReactNativeWebView) {
        if (window.kidsAppPlatform === "ios") {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener("message", handleMessage as EventListener);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex-col-el gap-6">
      <SubscriptionDialogs
        isStripeLoading={isStripeLoading}
        showUpdate={update}
        showStoreCancelInfo={showStoreCancelInfo}
        showPlatformMismatchError={showPlatformMismatchError}
        showAmazonPurchaseWarning={showAmazonPurchaseWarning}
        showRestoreSuccess={showRestoreSuccess}
        entitlement={entitlement}
        onCloseUpgrade={handleCloseUpgrade}
        onGotoUpgrade={handleGotoUpgrade}
        onCloseStoreCancelInfo={handleCloseStoreCancelInfo}
        onClosePlatformMismatchError={handleClosePlatformMismatchError}
        onCloseAmazonPurchaseWarning={handleCloseAmazonPurchaseWarning}
        onCloseRestoreSuccess={handleCloseRestoreSuccess}
      />

      <PlanInfoSection
        plan={plan}
        entitlement={entitlement}
        isPaid={isPaid}
        isEducation={isEducation}
        isNativeApp={isNativeApp}
        onManageSubscription={handleManageSubscription}
        onRestorePurchases={handleRestorePurchases}
      />

      {isFree && (
        <UpgradeSection
          billingPeriod={billingPeriod}
          packagesStatus={packagesStatus}
          isNativeApp={isNativeApp}
          onBillingPeriodChange={setBillingPeriod}
          onPurchase={handlePurchase}
          getPriceDisplay={getPriceDisplay}
        />
      )}

      <FeatureHeroCards />

      <PlanComparisonTable
        plan={plan}
        billingPeriod={billingPeriod}
        getPriceDisplay={getPriceDisplay}
      />
    </div>
  );
};
